import {
    createContext,
    FC,
    HTMLProps,
    ReactNode,
    useCallback,
    useRef,
    useState,
    MouseEvent as ReactMouseEvent,
} from "react"
import * as T from "trunx"
import cn from "classnames"
import { useDetectClickOutside } from "react-detect-click-outside"
import { Div } from "trunx"

import { useToggleClassOnHover } from "ui-kit/components-branded/hooks/setHoverHandler"
import { useQuerySelector } from "ui-kit/components-branded/hooks/useQuerySelector"
import { useFitHeight } from "ui-kit/components-branded/hooks/useFitHeight"
import { Icon } from "components"

interface ContextProps {
    clickHandler: () => void
    isCloseDropDownAfterClickItem?: boolean
}

const Context = createContext<ContextProps>(null!)

const Item: FC<HTMLProps<HTMLDivElement>> = ({ className, onClick, ...props }) => {
    const handleClickItem = (value: ReactMouseEvent<HTMLDivElement, MouseEvent>) => {
        if (onClick) {
            onClick(value)
        }
    }

    return <div {...props} className={cn("dropdown-item", className)} onClick={handleClickItem} />
}

interface Props extends Exclude<T.DropdownProps, "isActive"> {
    label?: string
    wrapperClassName?: string
    triggerClassName?: string
    isSelected?: boolean
    isDisabled?: boolean
    icon?: ReactNode
    offTriggerClassName?: boolean
    multiSelect?: boolean
    idContainer?: string
}

export function Dropdown({
    children,
    wrapperClassName,
    label,
    className,
    isSelected,
    isDisabled,
    icon,
    offTriggerClassName = false,
    triggerClassName,
    multiSelect,
    idContainer,
    ...props
}: Props) {
    const [nodeId, getNode] = useQuerySelector()
    useToggleClassOnHover(getNode, "is-hovered")
    const [isActive, setIsActive] = useState(false)
    const clickHandler = useCallback(() => {
        if (isDisabled) return
        setIsActive((isActive) => !isActive)
    }, [isDisabled])
    const ref = useDetectClickOutside({
        onTriggered: () => setIsActive(false),
    })
    const scrollableRef = useRef<HTMLDivElement>(null)
    useFitHeight({
        activate: isActive,
        nodeRef: scrollableRef,
        getStartNode: getNode,
        idContainer: idContainer,
    })

    return (
        <Context.Provider
            key={multiSelect ? "multiSelect" : `singleSelect-${label}`}
            value={{
                clickHandler,
            }}
        >
            <div ref={ref} className={cn("blm-select__wrapper", wrapperClassName)}>
                <T.Dropdown
                    {...props}
                    className={cn("blm-dropdown", className)}
                    isActive={isActive}
                >
                    <div
                        onClick={clickHandler}
                        className={cn(triggerClassName, {
                            "dropdown-trigger select blm-select blm-dropdown__trigger":
                                !offTriggerClassName,
                            "is-success": isSelected,
                            "is-disabled": isDisabled,
                        })}
                        id={nodeId}
                    >
                        {label && <span className="is-relative blm-dropdown__label">{label}</span>}
                        {!icon && (
                            <Icon
                                type="arrow-down"
                                size={24}
                                button
                                className="blm-dropdown__icon"
                            />
                        )}
                        {icon}
                    </div>
                    <T.DropdownMenu>
                        <Div bulma="dropdown-content">
                            <div className="blm-dropdown__scrollable" ref={scrollableRef}>
                                {children}
                            </div>
                        </Div>
                    </T.DropdownMenu>
                </T.Dropdown>
            </div>
        </Context.Provider>
    )
}

Dropdown.Item = Item
